import { useEffect } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useSelector } from 'react-redux'

import { Flex, Text, VStack } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import { DrawerScrollContainer } from '@/components/ui'
import { CheckpointIProps } from '@/features/floor-plan'
import { CheckpointListItem } from '@/features/guard-tour'
import { selectEditMode } from '@/redux/ui/uiSlice'

import { TourPlanHeader } from './TourPlanHeader'

interface TourPlanCheckPointsListIProps {
  onClose: () => void
  onCheckPointClick: (checkpoint: CheckpointIProps) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onEditClick: () => void
  isLoading: boolean
  name: string
  checkpoints: CheckpointIProps[]
}

export const TourPlanCheckPointsList = ({
  isLoading,
  name,
  checkpoints,
  onClose,
  onEditClick,
  onCheckPointClick,
  onDeviceItemHoverIn,
}: TourPlanCheckPointsListIProps) => {
  const isEditMode = useSelector(selectEditMode)
  const { setHighlightedDeviceIds } = useFloorPlan()

  const handleOnClick = (checkpoint: CheckpointIProps) => {
    onCheckPointClick(checkpoint)
  }

  const handleOnClose = () => {
    onClose()
    setHighlightedDeviceIds(null)
  }

  const handleDeviceItemHoverOut = () => {
    const checkpointIds = checkpoints?.map((c) => c?.id)
    setHighlightedDeviceIds(checkpointIds)
  }

  useEffect(() => {
    if (checkpoints) {
      const checkpointIds = checkpoints?.map((c) => c?.id)
      setHighlightedDeviceIds(checkpointIds)
    }
  }, [checkpoints])

  return (
    <>
      <Flex
        align='center'
        borderBottom='1px solid rgba(9, 30, 66, 0.14)'
        direction='column'
        padding='30px'
      >
        <TourPlanHeader
          count={checkpoints?.length}
          isLoading={false}
          name={name}
          onClose={handleOnClose}
          onEdit={onEditClick}
        />
      </Flex>
      <DrawerScrollContainer px='30px' py='20px'>
        <AnimatePresence>
          {isLoading ? (
            <Text
              animate={{ opacity: 1 }}
              as={motion.div}
              color='#9b9b9b'
              exit={{ opacity: 0 }}
              fontSize='14px'
              fontWeight={500}
              initial={{ opacity: 0 }}
              letterSpacing='-0.4px'
              lineHeight='16px'
              my='30px'
              textAlign='center'
            >
              Loading...
            </Text>
          ) : checkpoints?.length > 0 ? (
            <VStack align='stretch' spacing='10px'>
              {checkpoints.map((c) => (
                <CheckpointListItem
                  id={c.id}
                  isEditMode={isEditMode}
                  key={c.id}
                  label={c.name}
                  onClick={() => handleOnClick(c)}
                  onDeviceItemHoverIn={onDeviceItemHoverIn}
                  onDeviceItemHoverOut={handleDeviceItemHoverOut}
                  placed={c.isPlaced}
                  subLabel={c.displayId}
                />
              ))}
            </VStack>
          ) : (
            <Text
              animate={{ opacity: 1 }}
              as={motion.div}
              color='#9b9b9b'
              exit={{ opacity: 0 }}
              fontSize='14px'
              fontWeight={500}
              initial={{ opacity: 0 }}
              letterSpacing='-0.4px'
              lineHeight='16px'
              my='30px'
              textAlign='center'
            >
              List is empty
            </Text>
          )}
        </AnimatePresence>
      </DrawerScrollContainer>
    </>
  )
}
